import { FunctionalComponent, h } from '@stencil/core';
import { FluidFunctionalComponentProps } from '../model/functional/fluid-functional-component-props.interface';
import {
  FluidAlignment,
  FluidIconKey,
  FluidOption,
  FluidSize,
  normalise,
} from '@lmig/fluid-core';
import { Utils } from '../utils/utils';

interface DropdownMenuProps extends FluidFunctionalComponentProps {
  host?: any;
  menuId?: string;
  menuAlignment?: FluidAlignment;
  preventFocus?: boolean;
  dynamicWidth?: boolean;
  menuOpen: boolean;
  zIndex: number;
}

interface DropdownMenuOptListProps extends FluidFunctionalComponentProps {
  host: any;
  options: FluidOption[];
  groupLabel?: string;
  option?: FluidOption;
  listId?: string;
  emptyListMessage?: string;
  indent?: boolean;
}

// ================================================================ //
// -- Public Functional Components

/**
 * DropdownMenu
 */
export const DropdownMenu: FunctionalComponent<DropdownMenuProps> = (
  props: DropdownMenuProps,
  children
) => {
  const {
    host,
    menuId,
    menuOpen,
    menuAlignment,
    preventFocus,
    dynamicWidth,
    zIndex,
    ...rest
  } = props;

  return (
    <div
      {...rest}
      id={menuId}
      ref={(el) => host?.setMenuRef && host.setMenuRef(el)}
      onMouseDown={(event) => preventFocus && Utils.preventFocus(event)}
      style={{ 'z-index': `${zIndex}` }}
      class={{
        'fluid-dropdown-menu': true,
        'dropdown-menu--open': menuOpen,
        'dropdown-menu--closed': !menuOpen,
        'dropdown-menu--dynamic-width': !!dynamicWidth,
        'dropdown-menu--align-left': menuAlignment === FluidAlignment.LEFT,
        'dropdown-menu--align-center': menuAlignment === FluidAlignment.CENTER,
        'dropdown-menu--align-right': menuAlignment === FluidAlignment.RIGHT,
        [`${props.class}`]: !!props.class,
      }}>
      {children}
    </div>
  );
};

/**
 * DropdownMenuOptionsList
 */
export const DropdownMenuOptionsList: FunctionalComponent<
  DropdownMenuOptListProps
> = (props: DropdownMenuOptListProps) => {
  const { host, listId, options } = props;
  const { label, _optGroupLabels, _optionsUpdating, _visibleOptionCount } =
    host;

  const ungroupedOpts = options.filter((o) => !o.group?.label);

  return (
    <ul
      role='listbox'
      id={listId}
      aria-label={label ?? 'Options List'}
      class='dropdown-menu-list'>
      {!_optionsUpdating &&
        !!_optGroupLabels &&
        _optGroupLabels.map((label) => {
          // Render grouped options
          return <DropdownMenuOptGroup groupLabel={label} {...props} />;
        })}
      {!_optionsUpdating &&
        !!_visibleOptionCount &&
        ungroupedOpts.map((opt) => {
          // Render ungrouped options
          return <DropdownMenuOption option={opt} {...props} />;
        })}
      {host.customInputOptionVisible() && ( // Render "custom input" option
        <DropdownMenuCustomOption {...props} />
      )}
      {host.asyncLookupMessageVisible() && ( // Render initial async lookup message
        <DropdownMenuMessage
          {...props}
          emptyListMessage='Begin Typing to Search'
        />
      )}
      {_optionsUpdating && ( // Render "Searching" message
        <DropdownMenuMessage {...props} emptyListMessage='Searching...' />
      )}
      {host.noResultsMessageVisible() && ( // Render "No Results" message
        <DropdownMenuMessage {...props} />
      )}
    </ul>
  );
};

// ================================================================ //
// -- Private Functional Components

/**
 * DropdownMenuOptGroup
 * Renders group label, optgroup container, & grouped options
 */
const DropdownMenuOptGroup: FunctionalComponent<DropdownMenuOptListProps> = (
  props: DropdownMenuOptListProps
) => {
  const { host, groupLabel, options } = props;
  const groupLabelId = `${
    host.controlName && host.controlName + '-'
  }optgroup-${normalise(groupLabel)}`;
  const groupedOpts = options.filter(
    (o) => o.group?.label === groupLabel && !o._hidden
  );

  if (!groupedOpts.length) return;

  return (
    <li role='none' class='dropdown-menu-optgroup-container'>
      <span id={groupLabelId} class='dropdown-menu-optgroup-label'>
        {groupLabel}
      </span>
      <ul
        role='group'
        aria-labelledby={groupLabelId}
        class='dropdown-menu-optgroup'>
        {groupedOpts.map((opt) => (
          <DropdownMenuOption option={opt} indent {...props} />
        ))}
      </ul>
    </li>
  );
};

/**
 * DropdownMenuOption
 */
const DropdownMenuOption: FunctionalComponent<DropdownMenuOptListProps> = (
  props: DropdownMenuOptListProps
) => {
  const { host, option, indent } = props;

  // Don't render if option doesn't exist or is hidden
  if (!option || option._hidden) return;

  return (
    <li
      role='option'
      tabIndex={-1}
      title={option.tooltip}
      id={`${host.controlName && host.controlName + '-'}option-${
        option._index
      }`}
      key={`${host.controlName && host.controlName + '-'}option-${
        option._index
      }`}
      ref={(el) => host.setOptionRef(el, option._index)}
      onClick={() => host.handleOptionChange(option)}
      data-option-index={option._index}
      data-option-value={option.value}
      aria-selected={String(!!option.selected)}
      class={{
        'dropdown-menu-option': true,
        'dropdown-menu-option--selected': option.selected,
        'dropdown-menu-option--disabled': option.disabled,
        'dropdown-menu-option--active':
          option._index === host._activeIndex && !option.disabled,
        'dropdown-menu-option--indent': indent,
      }}>
      {!!host.multiSelect && <DropdownMenuCheckbox {...props} />}
      <span class='dropdown-menu-option-label'>{option.label}</span>
    </li>
  );
};

/**
 * DropdownMenuCheckbox
 */
const DropdownMenuCheckbox: FunctionalComponent<DropdownMenuOptListProps> = (
  props: DropdownMenuOptListProps
) => {
  const { option } = props;

  return (
    <input
      type='checkbox'
      role='presentation'
      class='dropdown-menu-option-checkbox'
      tabIndex={-1}
      disabled={option.disabled}
      checked={option.selected}
    />
  );
};

/**
 * DropdownMenuCustomOption
 */
const DropdownMenuCustomOption: FunctionalComponent<
  DropdownMenuOptListProps
> = (props: DropdownMenuOptListProps) => {
  const { host } = props;

  if (!host._elementRef) return;

  return (
    <li
      role='option'
      tabIndex={-1}
      id={`${host.controlName && host.controlName + '-'}option-custom`}
      key={`${host.controlName && host.controlName + '-'}option-custom`}
      ref={(el) => host.setOptionRef(el, host._visibleOptionCount)}
      onClick={() => host.handleCustomInput()}
      class={{
        'dropdown-menu-option': true,
        'dropdown-menu-option--active':
          host._activeIndex === host._visibleOptionCount,
      }}>
      <span class='dropdown-menu-option-label'>
        {`Select "${host._elementRef.value}"`}
      </span>
    </li>
  );
};

/**
 * DropdownMenuMessage
 */
const DropdownMenuMessage: FunctionalComponent<DropdownMenuOptListProps> = (
  props: DropdownMenuOptListProps
) => {
  const { host, emptyListMessage } = props;
  const { controlName, _optionsUpdating } = host;

  let messageId = `${!!controlName ? controlName + '-' : ''}option-message`;
  if (_optionsUpdating) {
    messageId += '-async-search';
  } else if (host.asyncLookupMessageVisible()) {
    messageId += '-async-initial';
  }

  return (
    <li
      role='presentation'
      id={messageId}
      key={messageId}
      class='dropdown-menu-option dropdown-menu-option--message'>
      {(_optionsUpdating || host.asyncLookupMessageVisible()) && (
        <fluid-icon iconKey={FluidIconKey.SEARCH} size={FluidSize.SMALL} />
      )}
      <span class='dropdown-menu-option-label'>{emptyListMessage}</span>
    </li>
  );
};
